import { useId } from "react"

import { omit, pick } from "lodash-es"

import { InputContextUserProps } from "../Input"

// Define which props belong to InputContext
const INPUT_CONTEXT_KEYS = [
  "name",
  "label",
  "readonly",
  "required",
  "loading",
  "disabled",
  "autoFocus",
  "hasError",
  "hasWarning",
  "unit"
] as const

// Internal context fields that should be excluded from restProps
const INTERNAL_CONTEXT_KEYS = [
  "inputRef",
  "descriptionIDs",
  "registerDescription",
  "unregisterDescription"
] as const

type InputContextKey = (typeof INPUT_CONTEXT_KEYS)[number]

export interface UseInputContextPropsResult<T> {
  inputContextProps: Pick<T, InputContextKey & keyof T> & {
    name?: string
    id: string
  }
  restProps: Omit<
    T,
    | InputContextKey
    | "id"
    | "inputRef"
    | "descriptionIDs"
    | "registerDescription"
    | "unregisterDescription"
  >
  id: string
}

/**
 * Hook to extract InputContext-related props and handle ID generation.
 * Useful for components that wrap Base-UI components and need to manually
 * provide InputContextProvider.
 *
 * @param props - Component props including optional `id`
 * @returns Object with inputContextProps, restProps, and the resolved inputID
 *
 * @example
 * ```tsx
 * const MyInput = (props) => {
 *   const { inputContextProps, restProps, inputID } = useInputContextProps(props)
 *
 *   return (
 *     <InputContextProvider {...inputContextProps}>
 *       <BaseComponent {...restProps} id={inputID} />
 *     </InputContextProvider>
 *   )
 * }
 * ```
 */
export function useInputContextProps<T extends Partial<InputContextUserProps>>(
  props: T
): UseInputContextPropsResult<T> {
  const generatedId = useId()
  const { id: propsID, ...remainingProps } = props
  const id = propsID || generatedId

  const inputContextProps = {
    ...pick(remainingProps, INPUT_CONTEXT_KEYS),
    id
  }

  const restProps = omit(remainingProps, [
    ...INPUT_CONTEXT_KEYS,
    ...INTERNAL_CONTEXT_KEYS
  ])

  return {
    inputContextProps,
    restProps,
    id
  } as UseInputContextPropsResult<T>
}
